import { type PropsWithChildren, type ReactNode, useRef } from 'react';
import { Button } from '@accure/ui';
import { useDebounceValue, useResizeObserver } from 'usehooks-ts';

import { Link } from '@/components/ui/link';
import type { CustomerConfig } from '@/entities/customer-config';
import { useFeatureFlag } from '@/hooks/use-feature-flag';

export type ChartCardLayoutProps = PropsWithChildren<{
  title: string;
  featureFlagKey: keyof CustomerConfig['featureFlags'];
  linkTo: string;
  buttonIcon: ReactNode;
  buttonLabel: string;
  showButton?: boolean;
}>;

export function ChartCardLayout({
  title,
  featureFlagKey,
  linkTo,
  buttonIcon,
  buttonLabel,
  showButton = true,
  children,
}: ChartCardLayoutProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver<HTMLDivElement>({ ref });
  const [debouncedWidth] = useDebounceValue(width, 100);
  const featureFlag = useFeatureFlag();

  return (
    <div
      ref={ref}
      className="grid w-full grid-rows-[min-content_1fr] gap-4 rounded-md border p-4"
    >
      <h2 className="mt-2 text-center text-xl font-medium tracking-tight text-gray-900">
        {title}
      </h2>

      <div
        key={debouncedWidth}
        className="mx-auto flex w-full flex-col justify-center"
      >
        {children}
      </div>

      {featureFlag(featureFlagKey) && showButton ? (
        <div className="flex justify-center">
          <Link to={linkTo}>
            <Button variant="outline">
              {buttonIcon}
              {buttonLabel}
            </Button>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
